import type { AdminUser, IconName } from '@l2u/web-shared';
import type { Branding, NavLabelKey } from '@l2u/shared/schemas';

/**
 * Pure navigation/authorisation model for the desktop admin sidebar.
 *
 * React-free and route-object-free for the same reason as the sibling
 * mobileNav.ts: which destinations a role may see, and which pathnames it may
 * reach, are decisions about *strings*, so they are unit-tested without a DOM
 * (this repo has no component test environment). AdminShell renders whatever
 * `navForRole` returns; App.tsx's RoleGate asks `pathAllowedForRole`.
 *
 * Hiding a row is presentation only — the API enforces the same boundary with
 * `requireAdminRole` (apps/api/src/middleware/adminAuth.ts). Neither is
 * sufficient alone: the nav stops a reviewer stumbling in, the middleware stops
 * them typing the URL or calling the endpoint directly.
 */

export type NavGroup = 'overview' | 'operate' | 'configure' | 'monitor' | 'admin';

export type NavRow = {
  to: string;
  label: string;
  icon: IconName;
  group: NavGroup;
  end?: boolean;
  /** Hidden from reviewers, and the matching path prefix is redirected away. */
  adminOnly?: boolean;
  /**
   * An `adminOnly` row that reviewers SEE, rendered disabled with a lock chip,
   * rather than one that vanishes. Set where the absence is more confusing than
   * the restriction — a reviewer who cannot find Reports concludes the product
   * has no reporting, which is the finding this flag answers. Purely
   * presentational: `pathAllowedForRole` and the API's requireAdminRole are
   * unchanged, so typing the URL still redirects and the endpoint still refuses.
   */
  visibleWhenLocked?: boolean;
};

// One array so a new destination shows up in the sidebar and the mobile More
// screen together, or in neither. Rows of the same group must stay adjacent —
// groupNav emits a header each time the group changes.
export const NAV: NavRow[] = [
  { to: '/', label: 'Dashboard', icon: 'dashboard', group: 'overview', end: true },
  // The officer's inbox: everything awaiting a decision, across all three
  // entity types. Leads `operate` because it is the first screen of the day.
  { to: '/queue', label: 'Review queue', icon: 'clock', group: 'operate' },
  { to: '/companies', label: 'Companies', icon: 'building', group: 'operate' },
  { to: '/drivers', label: 'Drivers', icon: 'users', group: 'operate' },
  { to: '/assets', label: 'Assets', icon: 'truck', group: 'operate' },
  {
    to: '/verifications',
    label: 'Document verification',
    icon: 'shield-check',
    group: 'operate',
  },
  {
    to: '/corrective-actions',
    label: 'Corrective actions',
    icon: 'clipboard-check',
    group: 'operate',
  },
  // Assigning an asset audit makes the acting admin the answerer, and the audit
  // lands here. The page existed already but only as a mobile tab, so without
  // this row a desktop admin has no way back to what they just assigned.
  { to: '/audits', label: 'My audits', icon: 'clipboard-check', group: 'operate' },
  { to: '/visitors', label: 'Visitors', icon: 'user-check', group: 'operate' },
  { to: '/expiring-documents', label: 'Expiring documents', icon: 'inbox', group: 'operate' },
  // Admin-only: broadcasting to the whole workforce is not a reviewer's call.
  // Mirrored by requireAdminRole on /api/bulk-emails.
  { to: '/bulk-email', label: 'Bulk email', icon: 'mail', group: 'operate', adminOnly: true },
  {
    to: '/questionnaires',
    label: 'Questionnaires',
    icon: 'list-checks',
    group: 'configure',
    adminOnly: true,
  },
  { to: '/documents', label: 'Documents', icon: 'file-text', group: 'configure' },
  {
    to: '/document-types',
    label: 'Document types',
    icon: 'clipboard-check',
    group: 'configure',
  },
  {
    to: '/training',
    label: 'Training',
    icon: 'graduation-cap',
    group: 'configure',
    adminOnly: true,
  },
  { to: '/library', label: 'Document library', icon: 'book', group: 'configure' },
  // Reachable by reviewers on purpose: /api/activity is region-scoped (see
  // routes/activity.ts), so a restricted reviewer sees their own regions'
  // history and nothing else. The Settings > Activity tab stays for admins who
  // already know it there.
  { to: '/activity', label: 'Activity log', icon: 'activity', group: 'monitor' },
  {
    to: '/reports',
    label: 'Reports',
    icon: 'bar-chart',
    group: 'monitor',
    adminOnly: true,
    visibleWhenLocked: true,
  },
  { to: '/settings', label: 'Settings', icon: 'settings', group: 'admin', adminOnly: true },
];

// Which NAV rows are renameable, keyed by pathname. NAV itself keeps the
// defaults as static labels — converting it to a function of branding would
// touch every consumer and its tests — and the shells override at render.
// Exported because mobileNav.ts's app-bar titles must rename exactly the same
// pathnames: two copies of this map would let the sidebar and the app bar
// disagree the day a third label becomes renameable.
export const NAV_LABEL_PATHS: Record<string, NavLabelKey> = {
  '/companies': 'companies',
  '/drivers': 'drivers',
};

/**
 * The label a shell should render for a NAV row: the tenant's override when one
 * is set and non-blank, the row's own label otherwise. Blank is treated as
 * unset because Settings clears an override by emptying its input.
 */
export function resolveNavLabel(row: NavRow, branding: Partial<Branding> | undefined): string {
  const key = NAV_LABEL_PATHS[row.to];
  if (!key) return row.label;
  const override = branding?.navLabels?.[key];
  if (typeof override === 'string' && override.trim().length > 0) return override;
  return row.label;
}

/** Chip rendered beside a locked row. */
export const NAV_LOCKED_CHIP = 'Admin only';

/** Tooltip explaining the lock, so the chip is not the whole explanation. */
export const NAV_LOCKED_TITLE = 'Reporting figures are restricted to administrators.';

/**
 * The sidebar/More rows a signed-in user's role may see. Admin-only rows are
 * dropped for reviewers unless flagged `visibleWhenLocked`, in which case the
 * shell renders them disabled — see `isNavRowLocked`.
 */
export function navForRole(role: AdminUser['role']): NavRow[] {
  if (role === 'admin') return NAV;
  return NAV.filter((row) => !row.adminOnly || row.visibleWhenLocked === true);
}

/** Whether a shell should render this row as a disabled, chipped entry. */
export function isNavRowLocked(row: NavRow, role: AdminUser['role']): boolean {
  return role !== 'admin' && row.adminOnly === true;
}

/**
 * Path prefixes a reviewer may not reach. `/questions` is here although
 * it has no NAV row of its own: QuestionBuilder is the Questionnaires page's
 * editing surface, and leaving it open would make the restriction decorative.
 */
export const ADMIN_ONLY_PATH_PREFIXES = [
  '/settings',
  '/questionnaires',
  '/questions',
  '/training',
  '/reports',
  '/bulk-email',
];

// Strip any query/hash and trailing slash so '/settings?tab=users' and
// '/settings/' are judged as '/settings' rather than slipping past
// the segment check below.
function normalisePath(pathname: string): string {
  const withoutSuffix = pathname.split(/[?#]/, 1)[0] ?? '';
  const trimmed = withoutSuffix.replace(/\/+$/, '');
  return trimmed === '' ? '/' : trimmed;
}

// Whole-segment comparison, so /reports never claims /reports-legacy.
function isUnder(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

/** Whether a role may open a pathname. Admins may open everything. */
export function pathAllowedForRole(role: AdminUser['role'], pathname: string): boolean {
  if (role === 'admin') return true;
  const path = normalisePath(pathname);
  return !ADMIN_ONLY_PATH_PREFIXES.some((prefix) => isUnder(path, prefix));
}
